import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { tokensMapping } from '@/utils/tokens';

import {
  CurrencyValue,
  Flex,
  BarChart,
  formatDatetime,
  formatNumber,
  DisplaySwitch
} from '@blockanalitica/ui';
import { useState, useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useChartPeriod } from '@/hooks';

export default function HistoricTabs() {
  const theme = useTheme();
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const [activeDisplayOption, setActiveDisplayOption] = useState('sky_volume');

  const displayOptions = useMemo(
    () => [
      { key: 'sky_volume', value: 'SKY Volume' },
      { key: 'mkr_volume', value: 'MKR Volume' }
    ],
    []
  );
  const [data, error] = useApi({
    url: '/migrations/mkr/historic-volume/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      switch (activeDisplayOption) {
        case 'mkr_volume':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('MKR'),
                iconSize: '0'
              }}
            />
          );
        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('SKY'),
                iconSize: '0'
              }}
            />
          );
      }
    }
    return formatValue;
  }, [activeDisplayOption]);

  const yScaleValueFormatter = (value) => {
    return formatNumber(Number(value));
  };

  const datasets = useMemo(() => {
    switch (activeDisplayOption) {
      case 'mkr_volume':
        return [
          {
            xField: 'date',
            yField: 'mkr_sky_to_mkr_volume',
            label: 'SKY to MKR',
            backgroundColor: theme.colors.chartUp
          },
          {
            xField: 'date',
            yField: 'mkr_mkr_to_sky_volume',
            label: 'MKR to SKY',
            backgroundColor: theme.colors.chartDown
          }
        ];
      case 'sky_volume':
        return [
          {
            xField: 'date',
            yField: 'sky_mkr_to_sky_volume',
            label: 'MKR to SKY',
            backgroundColor: theme.colors.chartUp
          },
          {
            xField: 'date',
            yField: 'sky_sky_to_mkr_volume',
            label: 'SKY to MKR',
            backgroundColor: theme.colors.chartDown
          }
        ];
    }
  }, [activeDisplayOption, theme.colors]);

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        <BarChart
          key={activeDisplayOption}
          data={data}
          datasets={datasets}
          valueFormatter={valueFormatter}
          yScaleValueFormatter={yScaleValueFormatter}
          timeFilter={timeFilter}
          config={config}
          subValueFormatter={subValueFormatter}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
